import React, { useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import {
  ATMSegment,
  ATMHeader,
  ATMGrid,
  ATMField,
  ATMInput,
  ATMSelect,
  ATMTextArea,
  ATMDivider,
} from 'shared-it-appmod-ui';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import FormFlagUpload from 'src/components/organisms/form-flag-upload/form-flag-upload.component';
import FormFlag from 'src/components/molecules/form-flag/form-flag.component';
import {
  DistrictType,
  DroneInternalFlightType,
  FileCategory,
} from 'src/constants';
import { getFlightTypeStatus } from 'src/selectors/flight-type.selector';
import { IRequestFormMethods } from 'src/models/request.model';
import { useDistrictContext } from 'src/contexts/district.context';
import { useFlightTypeContext } from 'src/contexts/flight-type.context';
import { actionTypes } from 'src/ducks/flight-type.duck';
import { actionTypes as districtActionTypes } from 'src/ducks/district.duck';
import { getDistrictStatus } from 'src/selectors/district.selector';
import Lang from 'src/libraries/language';
import { useSensitiveCommunitiesContext } from 'src/contexts/sensitive-communities.context';
import { useDroneAreasContext } from 'src/contexts/drone-areas.context';
import { useSensitiveCitiesContext } from 'src/contexts/sensitive-cities.context';
import FlightRequestFlightArea from '../../flight-request-flight-area/flight-request-flight-area.component';
import FlightRequestDroneSCGCND from '../../flight-request-dronescg-cnd/flight-request-dronescg-cnd.component';
import styles from '../flight-request-drone.module.scss';

const DRONE_CGI_FLIGHT_TYPES = new Set(
  Object.values(DroneInternalFlightType).map((value) =>
    value.toLowerCase().trim()
  )
);

type IProps = IRequestFormMethods & {
  isEdit?: boolean;
};

const FlightRequestDroneSDGE: React.FC<IProps> = ({
  control,
  errors,
  isEdit: _isEdit,
  ...props
}) => {
  const { state, actions } = useFlightTypeContext();
  const { state: districtState, actions: districtActions } =
    useDistrictContext();

  const { state: sCommunitiesState, actions: sCommunitiesActions } = useSensitiveCommunitiesContext();
  const { state: droneAreasState, actions: droneAreasActions } = useDroneAreasContext();
  const { state: sCitiesState, actions: sCitiesActions } = useSensitiveCitiesContext();
  const [sCityPamLead, setSCityPamLead] = useState<string | null>('');
  const [sCommunityPamLead, setSCommunityPamLead] = useState<string | null>('');

  useEffect(() => {
    if (sCommunitiesActions && sCitiesActions && droneAreasActions) {
      sCommunitiesActions.listGET();
      sCitiesActions.listGET();
      droneAreasActions.listGET();
    }
  }, [sCommunitiesActions, sCitiesActions, droneAreasActions]);

  const handleChange = useCallback(
    (value, type) => {
      if (type === 'city') {
        if (!value) {
          setSCityPamLead(null);
        } else {
          const sCity = sCitiesState.list.find((v) => v.sensitiveCity === value);
          setSCityPamLead(sCity.pamLead);
        }
        control.setValue('droneFlightSDGE.sensitiveCity', value);
      } else if (type === 'drone') {
        control.setValue('droneFlightSDGE.droneArea', value);
      } else {
        if (!value) {
          setSCommunityPamLead(null);
        } else {
          const sCommunity = sCommunitiesState.list.find((v) => v.sensitiveCommunity === value);
          setSCommunityPamLead(sCommunity.sensitiveCommunityPamLead);
        }
        control.setValue('droneFlightSDGE.sensitiveCommunity', value);
      }
    },
    [state, control, sCitiesState, sCommunitiesState]
  );

const [sensitiveCustomerFlag, setSensitiveCustomerFlag] = useState(control?.defaultValuesRef?.current?.droneFlightSDGE?.sensitiveFlag || false);
const flightType: string | undefined = useWatch({
  control,
  name: 'droneFlightSDGE.flightType',
});
const defaultSCityPamLead = sCitiesState.list.find((v) => v.sensitiveCity === control?.defaultValuesRef?.current?.droneFlightSDGE?.sensitiveCity)?.pamLead;
const defaultSCommunityPamLead = sCommunitiesState.list.find((v) => v.sensitiveCommunity === control?.defaultValuesRef?.current?.droneFlightSDGE?.sensitiveCommunity)?.sensitiveCommunityPamLead;

  useEffect(() => {
    actions.listGET();
    districtActions.listGET();
  }, [districtActions]);

  const status = getFlightTypeStatus(state, actionTypes.FLIGHT_TYPE_FETCH);
  const districtStatus = getDistrictStatus(
    districtState,
    districtActionTypes.DISTRICT_LIST_FETCH
  );

  return (
    <ATMSegment color="teal" className={styles.wrapper}>
      <ATMHeader>{Lang.TTL_DRONE_FLIGHT_SCG}</ATMHeader>
      <ATMGrid columns={4}>
        <ATMGrid.Row columns={2}>
          {' '}
          <ATMGrid.Column width="5">
            <ATMField
              name="droneFlightSDGE.flightType"
              label={<LabelRequired>{Lang.LBL_FLIGHT_TYPE}</LabelRequired>}
              as={ATMSelect}
              selectOnBlur={false}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={state.list.map((value) => ({
                key: value.flightTypeId,
                value: value.flightType,
                text: value.flightType,
              }))}
              search
              clearable
              error={errors.droneFlightSDGE?.flightType}
              onChange={([_, { value }]) => value}
              loading={!state.list.length && status.fetching}
              disabled={!state.list.length && status.fetching}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightSDGE.purpose"
              label={<LabelRequired>{Lang.LBL_PURPOSE}</LabelRequired>}
              as={ATMInput}
              maxLength={200}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.droneFlightSDGE?.purpose}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Column>
          <ATMField
            name="droneFlightSDGE.project"
            label={Lang.LBL_PROJECT}
            as={ATMInput}
            maxLength={50}
            placeholder={Lang.LBL_ENTER}
            control={control}
            error={errors.droneFlightSDGE?.project}
          />
        </ATMGrid.Column>
      </ATMGrid>
      <ATMGrid>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightSDGE.destination"
              label={
                <LabelRequired>{Lang.LBL_ADDRESS_CROSS_STREET}</LabelRequired>
              }
              as={ATMInput}
              maxLength={200}
              placeholder={Lang.LBL_ENTER}
              control={control}
              style={{ width: '74%' }}
              error={errors.droneFlightSDGE?.destination}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>
      <ATMGrid>
        <ATMGrid.Row columns="equal">
          <ATMGrid.Column>
            <ATMField
              name="droneFlightSDGE.district"
              label={
                <LabelRequired>
                  {Lang.LBL_FLIGHT_DISTRICT_DEPARTMENT}
                </LabelRequired>
              }
              as={ATMSelect}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={districtState.list
                .filter((value) => value.relationship === DistrictType.Company)
                .map((value) => ({
                  key: value.key,
                  value: value.value,
                  text: value.value,
                }))}
              search
              error={errors.droneFlightSDGE?.district}
              onChange={([_, { value }]) => value}
              loading={!districtState.list.length && districtStatus.fetching}
              disabled={!districtState.list.length && districtStatus.fetching}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightSDGE.asset"
              label={Lang.LBL_FACILITY_ASSET_NUMBER}
              as={ATMInput}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.droneFlightSDGE?.asset}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="droneFlightSDGE.circuit"
              label={<LabelRequired>{Lang.LBL_PIPELINE_NUMBER}</LabelRequired>}
              as={ATMInput}
              placeholder={Lang.LBL_ENTER}
              control={control}
              error={errors.droneFlightSDGE?.circuit}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_OTHER_INFORMATION}
      </ATMHeader>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <FormFlag
            name="droneFlightSDGE.nightFlag"
            label={Lang.LBL_NIGHT_FLIGHT}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
        {control?.defaultValuesRef?.current?.droneFlightSDGE?.isOldFlight ? (
          <>
            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightSDGE.sensitiveFlag"
                listName="droneFlightSDGE.sensitiveFiles"
                label={Lang.LBL_SENSITIVE_CUSTOMER}
                allowedTypes={[FileCategory.SensitiveCustomer]}
                control={control}
                errors={errors}
                setValue={setSensitiveCustomerFlag}
              />
            </ATMGrid.Column>
            {sensitiveCustomerFlag && (
              <ATMGrid columns={4}>
                <ATMGrid.Column>
                  <ATMField
                    name="droneFlightSDGE.sensitiveCity"
                    label={Lang.LBL_SENSITIVE_CITY}
                    placeholder={Lang.LBL_SELECT}
                    as={ATMSelect}
                    control={control}
                    options={sCitiesState.list.map((item) => ({
                      key: item.sensitiveCityId,
                      value: item.sensitiveCity,
                      text: item.sensitiveCity,
                    }))}
                    onChange={([_, { value }]) => {
                      handleChange(value, 'city');
                      return value;
                    }}
                    selectOnBlur={false}
                    error={errors?.droneFlightSDGE?.sensitiveCity}
                    loading={!state.list.length && status.fetching}
                    disabled={!state.list.length && status.fetching}
                    clearable
                  />
                  {!(sCityPamLead === null) &&
                    (sCityPamLead || defaultSCityPamLead) && (
                      <>
                        <div>City PAM Lead:</div>
                        <div>{sCityPamLead || defaultSCityPamLead}</div>
                      </>
                    )}
                </ATMGrid.Column>
                <ATMGrid.Column>
                  <ATMField
                    name="droneFlightSDGE.sensitiveCommunity"
                    label={Lang.LBL_SENSITIVE_COMMUNITIES}
                    placeholder={Lang.LBL_SELECT}
                    as={ATMSelect}
                    control={control}
                    options={sCommunitiesState.list.map((item) => ({
                      key: item.sensitiveCommunityId,
                      value: item.sensitiveCommunity,
                      text: item.sensitiveCommunity,
                    }))}
                    onChange={([_, { value }]) => {
                      handleChange(value, 'community');
                      return value;
                    }}
                    selectOnBlur={false}
                    error={errors?.droneFlightSDGE?.sensitiveCommunity}
                    loading={!state.list.length && status.fetching}
                    disabled={!state.list.length && status.fetching}
                    clearable
                  />
                  {!(sCommunityPamLead === null) &&
                    (sCommunityPamLead || defaultSCommunityPamLead) && (
                      <>
                        <div style={{ opacity: '80%' }}>
                          Community PAM Lead:
                        </div>
                        <div>
                          {sCommunityPamLead || defaultSCommunityPamLead}
                        </div>
                      </>
                    )}
                </ATMGrid.Column>
                <ATMGrid.Column>
                  <ATMField
                    name="droneFlightSDGE.droneArea"
                    label={Lang.LBL_DRONE_AREA}
                    placeholder={Lang.LBL_SELECT}
                    as={ATMSelect}
                    control={control}
                    options={droneAreasState.list.map((item) => ({
                      key: item.droneAreasId,
                      value: item.SensitiveCityLocation,
                      text: item.SensitiveCityLocation,
                    }))}
                    onChange={([_, { value }]) => {
                      handleChange(value, 'drone');
                      return value;
                    }}
                    selectOnBlur={false}
                    error={errors?.droneFlightSDGE?.droneArea}
                    loading={!state.list.length && status.fetching}
                    disabled={!state.list.length && status.fetching}
                    clearable
                  />
                </ATMGrid.Column>
              </ATMGrid>
            )}
          </>
        ) : (
          <>
            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightSDGE.safetyFlag"
                listName="droneFlightSDGE.safetyFiles"
                label={Lang.LBL_DRONE_OTHER_QUESTION_2}
                allowedTypes={[FileCategory.SafetyMitigations]}
                control={control}
                errors={errors}
                note={Lang.LBL_DRONE_OTHER_QUESTION_2_NOTE}
                no={false}
                na
              />
            </ATMGrid.Column>

            <ATMGrid.Column>
              <FormFlag
                name="droneFlightSDGE.sensitiveFlag"
                label={Lang.LBL_DRONE_OTHER_QUESTION_3}
                control={control}
                errors={errors}
                note={Lang.LBL_DRONE_OTHER_QUESTION_3_NOTE}
                no={false}
                na
                setValue={setSensitiveCustomerFlag}
              />
            </ATMGrid.Column>
            {sensitiveCustomerFlag && (
              <>
                <ATMGrid columns={4}>
                  <ATMGrid.Column>
                    <ATMField
                      name="droneFlightSDGE.sensitiveCity"
                      label={Lang.LBL_SENSITIVE_CITY}
                      placeholder={Lang.LBL_SELECT}
                      as={ATMSelect}
                      control={control}
                      options={sCitiesState.list.map((item) => ({
                        key: item.sensitiveCityId,
                        value: item.sensitiveCity,
                        text: item.sensitiveCity,
                      }))}
                      onChange={([_, { value }]) => {
                        handleChange(value, 'city');
                        return value;
                      }}
                      selectOnBlur={false}
                      error={errors?.droneFlightSDGE?.sensitiveCity}
                      loading={!state.list.length && status.fetching}
                      disabled={!state.list.length && status.fetching}
                      clearable
                    />
                    {!(sCityPamLead === null) &&
                      (sCityPamLead || defaultSCityPamLead) && (
                        <>
                          <div>City PAM Lead:</div>
                          <div>{sCityPamLead || defaultSCityPamLead}</div>
                        </>
                      )}
                  </ATMGrid.Column>

                  <ATMGrid.Column>
                    <ATMField
                      name="droneFlightSDGE.sensitiveCommunity"
                      label={Lang.LBL_SENSITIVE_COMMUNITIES}
                      placeholder={Lang.LBL_SELECT}
                      as={ATMSelect}
                      control={control}
                      options={sCommunitiesState.list.map((item) => ({
                        key: item.sensitiveCommunityId,
                        value: item.sensitiveCommunity,
                        text: item.sensitiveCommunity,
                      }))}
                      onChange={([_, { value }]) => {
                        handleChange(value, 'community');
                        return value;
                      }}
                      selectOnBlur={false}
                      error={errors?.droneFlightSDGE?.sensitiveCommunity}
                      loading={!state.list.length && status.fetching}
                      disabled={!state.list.length && status.fetching}
                      clearable
                    />
                    {!(sCommunityPamLead === null) &&
                      (sCommunityPamLead || defaultSCommunityPamLead) && (
                        <>
                          <div style={{ opacity: '80%' }}>
                            Community PAM Lead:
                          </div>
                          <div>
                            {sCommunityPamLead || defaultSCommunityPamLead}
                          </div>
                        </>
                      )}
                  </ATMGrid.Column>
                  <ATMGrid.Column>
                    <ATMField
                      name="droneFlightSDGE.droneArea"
                      label={Lang.LBL_DRONE_AREA}
                      placeholder={Lang.LBL_SELECT}
                      as={ATMSelect}
                      control={control}
                      options={droneAreasState.list.map((item) => ({
                        key: item.droneAreasId,
                        value: item.SensitiveCityLocation,
                        text: item.SensitiveCityLocation,
                      }))}
                      onChange={([_, { value }]) => {
                        handleChange(value, 'drone');
                        return value;
                      }}
                      selectOnBlur={false}
                      error={errors?.droneFlightSDGE?.droneArea}
                      loading={!state.list.length && status.fetching}
                      disabled={!state.list.length && status.fetching}
                      clearable
                    />
                  </ATMGrid.Column>
                </ATMGrid>
                {DRONE_CGI_FLIGHT_TYPES.has(
                  String(flightType || '').toLowerCase().trim()
                ) && (
                  <FlightRequestDroneSCGCND
                    category="droneFlightSDGE"
                    control={control}
                    errors={errors}
                    {...props}
                  />
                )}
              </>
            )}

            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightSDGE.landFlag"
                listName="droneFlightSDGE.landFiles"
                label={Lang.LBL_DRONE_OTHER_QUESTION_4}
                allowedTypes={[FileCategory.LandManagementApproval]}
                control={control}
                errors={errors}
                note={Lang.LBL_DRONE_OTHER_QUESTION_4_NOTE}
              />
            </ATMGrid.Column>

            <ATMGrid.Column>
              <FormFlagUpload
                name="droneFlightSDGE.culturalFlag"
                listName="droneFlightSDGE.culturalFiles"
                label={Lang.LBL_DRONE_OTHER_QUESTION_5}
                allowedTypes={[FileCategory.EnvironmentalCulturalRestrictions]}
                control={control}
                errors={errors}
                note={Lang.LBL_DRONE_OTHER_QUESTION_5_NOTE}
              />
            </ATMGrid.Column>
          </>
        )}
      </ATMGrid>

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_BRIEF_JOB_DESCRIPTION}
      </ATMHeader>
      <p>{Lang.MSG_BRIEF_DESCRIPTION}</p>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <ATMField
            name="droneFlightSDGE.jobDesc"
            label={Lang.LBL_DESCRIPTION_COMMENT}
            as={ATMTextArea}
            placeholder={Lang.LBL_ENTER}
            control={control}
            error={errors.droneFlightSDGE?.jobDesc}
            style={{
              width: '75%',
              height: '250px',
            }}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <FlightRequestFlightArea
        category="droneFlightSDGE"
        control={control}
        errors={errors}
      />
      <ATMDivider />

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_OTHER_SPECIAL_COMMENTS}
      </ATMHeader>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <ATMField
            name="droneFlightSDGE.comments"
            label={Lang.LBL_COMMENT}
            as={ATMTextArea}
            placeholder={Lang.LBL_ENTER}
            control={control}
            error={errors.droneFlightSDGE?.comments}
            style={{
              width: '75%',
              height: '250px',
            }}
          />
        </ATMGrid.Column>
      </ATMGrid>
    </ATMSegment>
  );
};

export default FlightRequestDroneSDGE;
